import { groupBy } from '@/utils/comm';
import moment from 'moment';
import { ChartDay } from '@/coreDomainsModule/table';
import { simulate } from '../business/inventorySimulation';
import { dateObjToUnixTime, dateStrToObject } from '@/appLogic/business/businessLogic';
import { MONTH_NUM_OF_BIZDAYS } from '@/coreDomainsModule/constants';
import _ from 'lodash';
import { IExtraSimulate } from '@/recommendOrderModule/pages/interfaces/extraSimulation';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { IDate } from '@/coreDomainsModule/date';
import { i18n } from '@/i18n';

const buildChartDataSet = (
  labels: string[],
  dailyInventoryLowerBounds: { [key: string]: number[] },
  dailyInventoryHigherBounds: { [key: string]: number[] },
  inventories: number[],
  willArrival: number[],
  announces: number[]
) => {
  const t = i18n.t;
  const output: {
    labels: string[];
    datasets: {
      label: string;
      data: number[];
      borderColor?: string;
      borderRadius?: number;
      backgroundColor?: string;
      borderWidth?: number;
      fill?: boolean;
      type?: string;
      lineTension?: number;
      order?: number;
      stack?: string; // 基本的に指定されていない場合、同じ積み上げグループになる
    }[];
  } = {
    // コメント
    labels: labels,
    datasets: [
      {
        label: t('在庫(詳細ページ)'),
        data: inventories,
        backgroundColor: '#1E92EF',
        borderRadius: 5,
        order: 2,
      },
      {
        label: t('入荷予定(詳細ページ)'),
        data: willArrival,
        backgroundColor: '#E65100',
        borderRadius: 5,
        order: 2,
      },
      {
        label: t('内示(詳細ページ)'),
        data: announces,
        backgroundColor: '#5E35B1',
        borderRadius: 5,
        order: 2,
      },
    ],
  };
  const keys = Object.keys(dailyInventoryLowerBounds);
  for (let i = 0; i < keys.length; i++) {
    const name = keys[i];
    output.datasets.push({
      label: t('標準在庫指数(下限)(詳細ページ)'),
      data: dailyInventoryLowerBounds[name],
      fill: false,
      type: 'line',
      backgroundColor: '#AD1457',
      lineTension: 0.3,
      borderColor: '#AD1457',
      borderWidth: 2,
      order: 1, // 前面に配置
      stack: 'line-1', // 積み上げたくないので、stackを指定
    });
    output.datasets.push({
      label: t('標準在庫指数(上限)(詳細ページ)'),
      data: dailyInventoryHigherBounds[name],
      fill: false,
      type: 'line',
      backgroundColor: '#4CAF50',
      lineTension: 0.3,
      borderColor: '#4CAF50',
      borderWidth: 2,
      order: 1, // 前面に配置
      stack: 'line-2', // 積み上げたくないので、stackを指定
    });
  }
  return output;
};

const parseChartData = (eachMonthData: { [key: string]: number }[], attr: string) => {
  const chartData: number[] = [];
  eachMonthData.forEach((value) => {
    chartData.push(value[attr] || 0);
  });
  return chartData;
};

/**
 * 『日』ごとのチャートの値をリアクティブに変化
 * @param business '
 * @param orderAmount '
 * @param bizDays '
 * @param differenceFromStartDate '
 * @param deliveryDate '
 * @param extraSimulate - シミュレーション用：extraSimulateを入れると、その情報を元にシミュレーションを行う(recommendの推奨は行わないようになる）
 * @param simulationStartDate - シミュレーション計算の開始日。指定すると、この日付より前のrecommendを抑制する
 */
const changeDayChart = async (
  business: IBusiness,
  orderAmount: number | null = null,
  bizDays: number,
  differenceFromStartDate: number,
  deliveryDate: IDate,
  extraSimulate?: IExtraSimulate,
  simulationStartDate?: Date
) => {
  const result = await simulate(
    business,
    bizDays,
    orderAmount,
    dateObjToUnixTime(deliveryDate || dateStrToObject(new Date().getTime())),
    differenceFromStartDate,
    extraSimulate,
    simulationStartDate
  );
  if (!result) return;
  const dailyInventoryLowerBounds = result.dailyInventoryLowerBounds;
  const dailyInventoryHigherBounds = result.dailyInventoryHigherBounds;
  const inventories: number[] = [];
  const willArrival: number[] = [];
  const announces: number[] = [];
  const days: ChartDay[] = [];
  const simData = result?.simData || [];
  simData.forEach((value) => {
    inventories.push(value.inventory || 0);
    willArrival.push(value.willArrival);
    announces.push(value.customerAnnounce || 0);
    days.push({ year: value.year || 0, month: (value.month || 0) - 1, day: value.day || 0 });
  });

  const labels = days.map((day) => moment(day).format('YYYY-MM-DD'));
  return buildChartDataSet(labels, dailyInventoryLowerBounds, dailyInventoryHigherBounds, inventories, willArrival, announces);
};

/**
 * 『月』ごとのチャートの値をリアクティブに変化(TODO: こっちは現状動作していないので修正が必要)
 * @param business '
 * @param orderAmount '
 * @param deliveryDate '
 * @param extraSimulate - シミュレーション用：extraSimulateを入れると、その情報を元にシミュレーションを行う(recommendの推奨は行わないようになる）
 */
const changeMonthChart = async (
  business: IBusiness,
  orderAmount: number | null = null,
  deliveryDate: IDate,
  extraSimulate?: IExtraSimulate
) => {
  const result = await simulate(
    business,
    MONTH_NUM_OF_BIZDAYS,
    orderAmount,
    dateObjToUnixTime(deliveryDate || dateStrToObject(new Date().getTime())),
    undefined,
    extraSimulate
  );
  if (!result) return;
  const simData = result?.simData || [];
  const dailyInventoryLowerBounds = result?.dailyInventoryLowerBounds || {};
  const dailyInventoryHigherBounds = result?.dailyInventoryHigherBounds || {};
  const eachMonthStandards = {};
  const simDataGroupBy = groupBy(simData, (simData) => simData.month);
  for (const name of Object.keys(dailyInventoryLowerBounds)) {
    eachMonthStandards[name] = [];
    const [month, result] = simDataGroupBy[0];
    for (let i = 0; i < simDataGroupBy.length; i++) {
      const [month, result] = simDataGroupBy[i];
      eachMonthStandards[name];
    }
    eachMonthStandards[name] = groupBy(simData, (simData) => simData.month).map(([month, results]) => ({
      month: month,
      dailyInventoryLowerBound: results.reduce((sum, item) => sum + item.dailyInventoryLowerBound, 0),
    }));
  }

  const eachMonthInventories = groupBy(simData, (simData) => simData.month).map(([month, results]) => ({
    month: month,
    inventory: results.reduce((sum, item) => sum + item.inventory!, 0),
  }));

  const eachMonthAnnounces = groupBy(simData, (simData) => simData.month).map(([month, results]) => ({
    month: month,
    announce: results.reduce((sum, item) => sum + item.customerAnnounce!, 0),
  }));

  const eachMonthWillArrivals = groupBy(simData, (simData) => simData.month).map(([month, results]) => ({
    month: month,
    willArrival: results.reduce((sum, item) => sum + item.willArrival!, 0),
  }));

  const inventories = parseChartData(eachMonthInventories, 'inventory');
  const announces = parseChartData(eachMonthAnnounces, 'announce');
  const willArrival = parseChartData(eachMonthWillArrivals, 'willArrival');
  const days: ChartDay[] = [];
  simData.forEach((value) => {
    days.push({ year: value.year || 0, month: value.month, day: value.day || 0 });
  });
  const labels = _.uniqBy(days, (x) => x.month).map((day) => `${day.year}-${String(day.month).length > 1 ? day.month : '0' + day.month}`);

  return buildChartDataSet(labels, dailyInventoryLowerBounds, dailyInventoryHigherBounds, inventories, announces, willArrival);
};

export { changeDayChart, changeMonthChart };
